import { ROUTES } from 'consts'
import { Roles } from 'consts/authConsts'
import usePlanLimits from 'hooks/usePlanLimits'
import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useFetchTxTrackingAggregationQuery } from 'store/modules/TxTrackingReport'
import { trackEvent } from 'utils/analyticsUtils'
import { isUserOfAnyAdminRole, isUserOfRole } from 'utils/authUtils'
import { isMobile } from 'utils/mobileUtils'
import { getTxTrackingReportPastXDays, setTxTrackingReportPastXDays } from 'utils/storageUtils'
import useCopilotAccountOwnerId from '../../hooks/useCopilotAccountOwnerId'
import useCopilotReports, { CopilotReports, buildCopilotReportPath } from '../../hooks/useCopilotReports'

const DEFAULT_PAST_X_DAYS = 7

export default () => {
  const history = useHistory()
  const enabledReports = useCopilotReports()
  const { grinPlanKey } = usePlanLimits()
  const accountOwnerId = useCopilotAccountOwnerId()
  const { doctor } = useSelector(state => state.profileReducer)
  const { queryParams } = useQueryParams()
  const isDevAdmin = isUserOfRole([Roles.DevAdmin])
  const accountOwnerFeatureFlags = useSelector(state => state.practiceReducer?.accountOwner?.user?.featureFlags?.flags)
  const wasOpenTracked = useRef(false)

  // First visit defaults to 7D; later visits restore the Tx Tracking selection.
  const [pastXDays, setPastXDaysState] = useState(() => {
    const stored = getTxTrackingReportPastXDays()
    return stored !== undefined ? stored : DEFAULT_PAST_X_DAYS
  })

  const setPastXDays = useCallback(updatedPastXDays => {
    setPastXDaysState(updatedPastXDays)
    setTxTrackingReportPastXDays(updatedPastXDays)
  }, [])

  const {
    data: aggregationData,
    isFetching: isAggregationLoading,
    isUninitialized: isAggregationUninitialized,
    isError: isAggregationError
  } = useFetchTxTrackingAggregationQuery({ accountOwnerId, pastXDays }, { skip: !accountOwnerId })

  useEffect(() => {
    if (isMobile()) {
      history.push(ROUTES.PATIENTS)
      return
    }

    if (isDevAdmin && queryParams.copilotDoctorId) {
      return
    }

    // Same eligibility wait as the other reports: an admin session never receives grinPlanKey
    // without a practice context, so its plan counts as resolved.
    const isPlanResolved = !!grinPlanKey || isUserOfAnyAdminRole()
    const isEligibilityResolved = !!doctor?.id && isPlanResolved && typeof accountOwnerFeatureFlags === 'string'
    if (!isEligibilityResolved) {
      return
    }

    // A disabled deep link redirects to an enabled sibling report, otherwise to Patients.
    // enabledReports holds each report's flag and plan requirement.
    if (!enabledReports.some(report => report.key === CopilotReports.TxTracking)) {
      const [firstEnabledReport] = enabledReports
      history.replace(
        firstEnabledReport
          ? buildCopilotReportPath(firstEnabledReport.route, queryParams.copilotDoctorId)
          : ROUTES.PATIENTS
      )
    }
  }, [isDevAdmin, queryParams, history, enabledReports, doctor?.id, grinPlanKey, accountOwnerFeatureFlags])

  useEffect(() => {
    if (accountOwnerId && !wasOpenTracked.current) {
      wasOpenTracked.current = true
      trackEvent('Tx Tracking Report - Opened', { pastXDays })
    }
  }, [accountOwnerId, pastXDays])

  return {
    accountOwnerId,
    pastXDays,
    setPastXDays,
    aggregationData,
    isAggregationLoading: isAggregationLoading || isAggregationUninitialized,
    isAggregationError
  }
}
